// src/pages/Upload.jsx
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { useToast } from "../components/Toast/useToast.jsx";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { usePermissions } from "../hooks/usePermissions";
import { MODULES, ACTIONS } from "../utils/permissions";
import { DocumentTypeGuard, Can } from "../components/PermissionGuard";
import ProgressBar from "../components/ProgressBar";
import {
  storage,
  db,
  serverTimestamp,
  auth,
  authReady,
  appCheckReady,
  ensureAppCheckToken,
} from "../firebase";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  doc,
  setDoc,
  collection,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
} from "firebase/firestore";

// Import utilities
import {
  isValidEmail,
  isValidPhone,
  validatePdfFile,
  normalizeContactId,
  getExpirationDaysFromNow,
  buildSignatureLink,
  buildInvitationEmail,
  buildWhatsAppLink,
  buildInvitationMessage,
  shareViaWhatsApp,
  getQrPdfFilename,
  FILE_CONSTRAINTS,
  COLLECTIONS,
} from "../utils";

export default function Upload() {
  const { showToast } = useToast();
  const { companyId, user } = useAuth();
  const { getSignatureConfig } = useCompany();
  const { 
    canCreateDocumentType, 
    getAllowedDocumentTypes,
    hasPermission 
  } = usePermissions();
  
  // 🔧 Detectar si viene desde solicitud
  const [searchParams] = useSearchParams();
  const fromSolicitud = searchParams.get('from') === 'solicitud';
  const requestType = searchParams.get('type'); // 'albaran' o 'parte_trabajo'
  
  // 🔧 Get allowed document types based on permissions
  const allowedTypes = getAllowedDocumentTypes();
  
  // 🔧 Tipo de documento: "albaran" o "parte_trabajo"
  const getInitialDocumentType = () => {
    // Si viene desde solicitud, el tipo de la URL tiene prioridad absoluta
    if (fromSolicitud && requestType === 'parte_trabajo') return 'parte_trabajo';
    if (fromSolicitud && requestType === 'albaran') return 'albaran';
    if (allowedTypes.includes('albaran')) return 'albaran';
    if (allowedTypes.includes('parte_trabajo')) return 'parte_trabajo';
    return 'albaran';
  };
  
  const [documentType, setDocumentType] = useState(getInitialDocumentType());

  // Cuando los permisos terminen de cargar, corregir el tipo si viene desde solicitud
  useEffect(() => {
    if (!fromSolicitud || !requestType) return;
    if (requestType === 'parte_trabajo') {
      setDocumentType('parte_trabajo');
    } else if (requestType === 'albaran') {
      setDocumentType('albaran');
    }
  }, [fromSolicitud, requestType]);
  
  // 🏗️ Campo obra para partes de trabajo (para agrupar)
  const [obra, setObra] = useState("");
  
  // 🔢 Número de serie para albaranes
  const [numeroSerie, setNumeroSerie] = useState("");
  
  // 🛡️ Datos de la solicitud (para acceder a PRL)
  const [solicitudData, setSolicitudData] = useState(null);
  
  const [files, setFiles] = useState([]); // 📁 Array de archivos
  const [uploadedDocs, setUploadedDocs] = useState([]); // 📋 Documentos subidos con sus enlaces
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // {fileName: progress%}
  const [drag, setDrag] = useState(false);

  // destinatarios (reciben invitación a firmar y, si mantienes tu lógica, también el aviso final)
  const [destValue, setDestValue] = useState("");
  const [destName, setDestName] = useState("");
  const [saveContact, setSaveContact] = useState(true);
  const [recipients, setRecipients] = useState([]); // {type:'email'|'phone', value, name?}
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);

  // ✅ emails del cliente que SOLO recibirán el PDF cuando esté firmado
  const [finalEmailValue, setFinalEmailValue] = useState("");
  const [finalEmails, setFinalEmails] = useState([]); // array de strings

  // mensaje
  const defaultMsg =
    "Hola, por favor firma este documento para confirmar la entrega.\n\nGracias.";
  const [message, setMessage] = useState(defaultMsg);

  // estado envío
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState({}); // { email: 'ok'|'error' }

  const inputRef = useRef(null);
  const SEND_URL = import.meta.env.VITE_SEND_EMAIL_URL?.trim();
  const APPEND_QR_URL = import.meta.env.VITE_APPEND_QR_URL?.trim();

  // ===== Cargar datos de solicitud si viene desde ahí =====
  useEffect(() => {
    if (fromSolicitud) {
      const requestId = searchParams.get('requestId');
      
      if (requestId) {
        // Cargar desde Firestore usando el requestId
        loadSolicitudFromFirestore(requestId);
      } else {
        // Cargar desde localStorage (método anterior)
        loadSolicitudFromLocalStorage();
      }
    }
  }, [fromSolicitud]);

  const loadSolicitudFromFirestore = async (requestId) => {
    try {
      await Promise.all([authReady, appCheckReady]);
      await ensureAppCheckToken();
      
      // Determinar la colección basada en el tipo de solicitud
      const collection_name = requestType === 'albaran' ? 'albaran_requests' : 'work_requests';
      const docRef = doc(db, collection_name, requestId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // 🛡️ Guardar datos completos de la solicitud (incluye PRL) — con id incluido
        setSolicitudData({ id: docSnap.id, ...data });
        
        if (requestType === 'albaran') {
          // Pre-rellenar datos para albaranes
          setNumeroSerie(data.numeroSerie || "");
          
          // Pre-rellenar contacto del destinatario (teléfono)
          if (data.nombreDestinatario || data.personaContacto) {
            const nombreContacto = data.nombreDestinatario || data.personaContacto;
            const telefono = data.contactoDestinatario || data.telefonoContacto;
            
            if (telefono) {
              const phoneRecipient = {
                type: "phone",
                value: telefono,
                name: nombreContacto
              };
              setRecipients(prev => [...prev, phoneRecipient]);
            }
          }

          // 📧 Pre-rellenar email del cliente en finalEmails
          if (data.emailCliente) {
            setFinalEmails(prev =>
              prev.includes(data.emailCliente) ? prev : [...prev, data.emailCliente]
            );
          }
          
          // 🔗 Mensaje especial si viene desde Kanban
          const origenKanban = data.origen === 'kanban-cejm';
          const prefijoMensaje = origenKanban 
            ? `[Solicitud desde Kanban CEJM${data.kanban_card_id ? ` - Tarjeta ${data.kanban_card_id}` : ''}]\n\n`
            : '';
          
          // Pre-rellenar mensaje personalizado para albaranes
          const customMessage = `${prefijoMensaje}Estimado/a ${data.personaContacto || 'Cliente'},

Le enviamos el albarán correspondiente a:
- Pedido: ${data.numeroPedido || 'N/A'}
- Número de Serie: ${data.numeroSerie || 'N/A'}
${data.codigoObra ? `- Obra: ${data.codigoObra}` : ''}
- Técnico Responsable: ${data.tecnicoResponsable || data.requesterName || 'N/A'}
${data.tecnicoSolicitante && data.tecnicoSolicitante !== data.tecnicoResponsable ? `- Técnico Solicitante: ${data.tecnicoSolicitante}` : ''}
${data.tipoTransporte ? `- Transporte: ${data.tipoTransporte}` : ''}
${data.condicionesEspeciales ? `- Condiciones especiales: ${data.condicionesEspeciales}` : ''}
${data.observaciones ? `- Observaciones: ${data.observaciones}` : ''}

Por favor, revise el documento y proceda con la firma digital en el siguiente enlace:
{link}

Saludos cordiales,
Equipo CEJM`;
          
          setMessage(customMessage);
          
          // Mostrar notificación indicando el origen
          const origenText = origenKanban ? ' [🔗 Kanban]' : '';
          showToast({ 
            type: "success", 
            message: `Datos cargados desde solicitud de albarán${origenText}` 
          });
          
        } else {
          // Pre-rellenar datos para partes de trabajo (código existente)
          setObra(data.codigoObra || "");
          
          // Pre-rellenar contacto si existe (solo teléfono real, sin email falso)
          if (data.personaContacto) {
            if (data.telefonoContacto) {
              const phoneRecipient = {
                type: "phone",
                value: data.telefonoContacto,
                name: data.personaContacto
              };
              setRecipients(prev => [...prev, phoneRecipient]);
            }
          }

          // 👥 Pre-rellenar contactos de operarios si existen en la solicitud
          if (data.selectedOperarios && data.selectedOperarios.length > 0) {
            const operariosRecipients = data.selectedOperarios.map(operario => ({
              type: "email",
              value: operario.email,
              name: operario.nombre
            }));
            
            setRecipients(prev => {
              // Evitar duplicados
              const existing = prev.map(r => r.value.toLowerCase());
              const newOnes = operariosRecipients.filter(r => 
                !existing.includes(r.value.toLowerCase())
              );
              return [...prev, ...newOnes];
            });
          }
          
          // 📧 Pre-rellenar email del cliente en finalEmails
          if (data.emailCliente) {
            setFinalEmails(prev => 
              prev.includes(data.emailCliente) ? prev : [...prev, data.emailCliente]
            );
          }

          // Pre-rellenar mensaje personalizado para partes de trabajo
          const operariosInfo = data.selectedOperarios && data.selectedOperarios.length > 0 
            ? `\nOperarios asignados:\n${data.selectedOperarios.map(op => `- ${op.nombre} (${op.categoria} - ${op.especialidad})`).join('\n')}`
            : '';

          const customMessage = `Hola,

Por favor firma este parte de trabajo para la obra: ${data.codigoObra}

Detalles del trabajo:
- Partida: ${data.partidaTrabajo}
- Fecha de actuación: ${data.fechasActuacion}
- Operarios: ${data.operariosAsignados}${operariosInfo}
${data.numeroOferta ? `- Nº Oferta: ${data.numeroOferta}` : ''}

Gracias por tu colaboración.

Saludos,
${data.requesterName}`;
        
          setMessage(customMessage);
        }
        
        // NO mostrar toast aquí si es albarán (ya se muestra arriba con el origen)
        if (requestType !== 'albaran') {
          showToast({ 
            type: "success", 
            message: `Datos cargados desde solicitud de ${requestType === 'albaran' ? 'albarán' : (data.requesterName || 'parte de trabajo')}${data.selectedOperarios ? ` (${data.selectedOperarios.length} operarios)` : ''}` 
          });
        }
      } else {
        showToast({ 
          type: "warning", 
          message: "No se encontró la solicitud, usando datos básicos" 
        });
      }
    } catch (e) {
      console.error("Error cargando solicitud desde Firestore:", e);
      showToast({ 
        type: "warning", 
        message: "Error cargando datos de la solicitud" 
      });
    }
  };

  const loadSolicitudFromLocalStorage = () => {
    const solicitudData = localStorage.getItem('solicitudData');
    if (solicitudData) {
      try {
        const data = JSON.parse(solicitudData);
        
        // Pre-rellenar obra
        setObra(data.codigoObra || "");
        
        // Pre-rellenar contacto si existe
        if (data.personaContacto) {
          const contactEmail = `${data.personaContacto.toLowerCase().replace(/\s+/g, '.')}@cliente.com`;
          
          const newRecipient = {
            type: "email",
            value: contactEmail,
            name: data.personaContacto
          };
          setRecipients([newRecipient]);
          
          if (data.telefonoContacto) {
            const phoneRecipient = {
              type: "phone",
              value: data.telefonoContacto,
              name: data.personaContacto
            };
            setRecipients(prev => [...prev, phoneRecipient]);
          }
        }

        // 👥 Pre-rellenar contactos de operarios seleccionados
        if (data.selectedOperarios && data.selectedOperarios.length > 0) {
          const operariosRecipients = data.selectedOperarios.map(operario => ({
            type: "email",
            value: operario.email,
            name: operario.nombre
          }));
          
          setRecipients(prev => {
            // Evitar duplicados
            const existing = prev.map(r => r.value.toLowerCase());
            const newOnes = operariosRecipients.filter(r => 
              !existing.includes(r.value.toLowerCase())
            );
            return [...prev, ...newOnes];
          });
        }
        
        // Pre-rellenar mensaje personalizado
        const operariosInfo = data.selectedOperarios && data.selectedOperarios.length > 0 
          ? `\nOperarios asignados:\n${data.selectedOperarios.map(op => `- ${op.nombre} (${op.categoria} - ${op.especialidad})`).join('\n')}`
          : '';

        const customMessage = `Hola,

Por favor firma este parte de trabajo para la obra: ${data.codigoObra}

Detalles del trabajo:
- Partida: ${data.partidaTrabajo}
- Fecha de actuación: ${data.fechasActuacion}
- Operarios: ${data.operariosAsignados}${operariosInfo}
${data.numeroOferta ? `- Nº Oferta: ${data.numeroOferta}` : ''}

Gracias por tu colaboración.

Saludos,
${data.requesterName}`;
        
        setMessage(customMessage);
        
        showToast({ 
          type: "success", 
          message: `Datos cargados desde solicitud de ${data.requesterName}${data.selectedOperarios ? ` (${data.selectedOperarios.length} operarios)` : ''}` 
        });
        
        // Limpiar localStorage después de usar
        localStorage.removeItem('solicitudData');
      } catch (e) {
        console.error("Error cargando datos de solicitud:", e);
      }
    }
  };

  // ===== Cargar contactos (filtrados por empresa) =====
  useEffect(() => {
    (async () => {
      try {
        setContactsLoading(true);
        await Promise.all([authReady, appCheckReady]);
        await ensureAppCheckToken();
        
        // Cargar contactos SIN orderBy para evitar problemas de índices
        let qy;
        if (companyId) {
          // 🏢 Multi-tenant: Filtrar contactos por companyId
          qy = query(
            collection(db, COLLECTIONS.CONTACTS), 
            where("companyId", "==", companyId)
          );
        } else {
          // Sin filtro de empresa - cargar todos
          qy = collection(db, COLLECTIONS.CONTACTS);
        }
        
        const snap = await getDocs(qy);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // Ordenar en el cliente por fecha (más reciente primero)
        list.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
        
        setContacts(list);
        console.log(`✅ Contactos cargados: ${list.length}${companyId ? ` (empresa: ${companyId})` : ''}`);
      } catch (e) {
        console.error("❌ Error cargando contactos:", e);
        // Si falla, simplemente dejamos la lista vacía
        setContacts([]);
        showToast({ type: "warning", message: "No se pudieron cargar los contactos guardados" });
      } finally {
        setContactsLoading(false);
      }
    })();
  }, [companyId]);

  // File handlers - Ahora maneja múltiples archivos y permite acumular
  const onFiles = (fileList, shouldAccumulate = false) => {
    console.log('📁 onFiles llamado con:', fileList);
    console.log('📁 Número de archivos:', fileList?.length);
    console.log('📁 Acumular:', shouldAccumulate);
    
    if (!fileList || fileList.length === 0) {
      console.log('❌ No hay archivos en fileList');
      return;
    }
    
    const validFiles = [];
    const errors = [];
    
    // Validar cada archivo
    Array.from(fileList).forEach(f => {
      console.log(`🔍 Validando: ${f.name}`);
      
      // Verificar si el archivo ya existe (por nombre)
      const alreadyExists = files.some(existing => existing.name === f.name);
      if (alreadyExists && shouldAccumulate) {
        console.log(`⚠️ Ya existe: ${f.name}`);
        errors.push(`${f.name}: Ya está en la lista`);
        return;
      }
      
      const validation = validatePdfFile(f, FILE_CONSTRAINTS.MAX_SIZE_BYTES);
      if (validation.valid) {
        validFiles.push(f);
        console.log(`✅ Válido: ${f.name}`);
      } else {
        errors.push(`${f.name}: ${validation.error}`);
        console.log(`❌ Inválido: ${f.name} - ${validation.error}`);
      }
    });
    
    console.log(`📊 Resultado: ${validFiles.length} válidos, ${errors.length} errores`);
    
    if (errors.length > 0) {
      showToast({ 
        type: "warning", 
        message: `${errors.length} archivo(s) rechazado(s). ${errors[0]}` 
      });
    }
    
    if (validFiles.length > 0) {
      if (shouldAccumulate) {
        // Agregar a los archivos existentes
        setFiles(prev => [...prev, ...validFiles]);
        showToast({ 
          type: "success", 
          message: `${validFiles.length} archivo(s) agregado(s). Total: ${files.length + validFiles.length}` 
        });
      } else {
        // Reemplazar archivos existentes
        setFiles(validFiles);
        // Reset estados relacionados
        setUploadedDocs([]);
        setUploadProgress({});
        showToast({ 
          type: "success", 
          message: `${validFiles.length} archivo(s) seleccionado(s)` 
        });
      }
    }
  };

  // Función para remover un archivo de la lista
  const removeFile = (fileName) => {
    setFiles(prev => prev.filter(f => f.name !== fileName));
    showToast({ 
      type: "info", 
      message: `Archivo ${fileName} eliminado` 
    });
  };

  // Añadir destinatario manual (invitación / whatsapp)
  const addRecipient = async () => {
    const raw = destValue.trim();
    if (!raw) return;

    let type = null;
    if (isValidEmail(raw)) type = "email";
    else if (isValidPhone(raw)) type = "phone";
    else {
      showToast({ type: "error", message: "Introduce un correo válido o un teléfono" });
      return;
    }

    if (recipients.some(r => r.value.toLowerCase() === raw.toLowerCase())) {
      showToast({ type: "warning", message: "Ese destinatario ya está en la lista" });
      return;
    }

    const item = { type, value: raw, name: destName.trim() || undefined };
    setRecipients(prev => [...prev, item]);

    if (saveContact) {
      try {
        await Promise.all([authReady, appCheckReady]);
        await ensureAppCheckToken();
        const id = normalizeContactId(raw);
        await setDoc(
          doc(db, COLLECTIONS.CONTACTS, id),
          {
            id,
            type,
            value: raw,
            name: item.name || null,
            companyId: companyId || null,  // 🏢 Multi-tenant: ID de la empresa
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );

        setContacts(prev => {
          const exists = prev.find(c => c.id === id);
          const newOne = { id, ...item };
          return exists ? prev.map(c => c.id === id ? newOne : c) : [newOne, ...prev];
        });
      } catch (e) {
        console.error("save contact error:", e);
      }
    }

    setDestValue("");
    setDestName("");
  };

  // Añadir desde contactos guardados
  const pickContact = (c) => {
    if (!c) return;
    if (recipients.some(r => r.value.toLowerCase() === c.value.toLowerCase())) return;
    setRecipients(prev => [...prev, { type: c.type, value: c.value, name: c.name }]);
  };

  const removeRecipient = (value) => {
    setRecipients(prev => prev.filter(r => r.value !== value));
  };

  // Añadir email del cliente (solo final)
  const addFinalEmail = async () => {
    const v = finalEmailValue.trim();
    if (!v) return;
    if (!isValidEmail(v)) {
      showToast({ type: "error", message: "Introduce un correo válido" });
      return;
    }
    if (finalEmails.includes(v)) {
      showToast({ type: "warning", message: "Ese correo ya está en la lista" });
      return;
    }
    
    // Añadir a la lista
    setFinalEmails(prev => [...prev, v]);
    setFinalEmailValue("");
    
    // Guardar automáticamente como contacto
    try {
      const contactId = normalizeContactId(v);
      const contactRef = doc(db, COLLECTIONS.CONTACTS, contactId);
      await setDoc(contactRef, {
        id: contactId,
        type: "email",
        value: v,
        name: "Cliente", // Nombre por defecto
        companyId: companyId || null, // 🏢 Multi-tenant
        createdAt: serverTimestamp(),
      }, { merge: true });
      
      // Actualizar lista de contactos
      setContacts(prev => {
        const exists = prev.some(c => c.id === contactId);
        if (exists) return prev;
        return [...prev, { id: contactId, type: "email", value: v, name: "Cliente" }];
      });
    } catch (e) {
      console.error("Error guardando contacto:", e);
      // No mostramos error al usuario, el correo ya se añadió a la lista
    }
  };
  const removeFinalEmail = (v) => {
    setFinalEmails(prev => prev.filter(x => x !== v));
  };

  // Subir + crear solicitud - Ahora maneja múltiples archivos
  const handleUpload = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      showToast({ type: "error", message: "Selecciona al menos un PDF" });
      return;
    }

    try {
      setLoading(true);
      setUploadProgress({});
      setUploadedDocs([]);

      // ✅ auth + appCheck antes de tocar Storage
      await Promise.all([authReady, appCheckReady]);
      await ensureAppCheckToken();

      // 📋 Obtener requestId si viene desde solicitud
      const requestId = searchParams.get('requestId');
      
      console.log(`🚀 Subiendo ${files.length} archivo(s)...`);
      
      const uploadedResults = [];

      // Procesar cada archivo
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileNum = i + 1;
        
        try {
          console.log(`📄 [${fileNum}/${files.length}] Procesando: ${file.name}`);
          
          const id = uuid();
          const path = `uploads/${id}.pdf`;

          // Subida con progreso individual
          const storageRef = ref(storage, path);
          const task = uploadBytesResumable(storageRef, file, {
            contentType: file.type || "application/pdf",
          });

          await new Promise((resolve, reject) => {
            task.on(
              "state_changed",
              (s) => {
                const pct = Math.round((s.bytesTransferred / s.totalBytes) * 100);
                setUploadProgress(prev => ({
                  ...prev,
                  [file.name]: pct
                }));
              },
              reject,
              resolve
            );
          });

          const downloadURL = await getDownloadURL(storageRef).catch(() => null);

          // Generar número de serie para albaranes
          const generatedNumeroSerie = documentType === "albaran" 
            ? `ALB-${Date.now().toString().slice(-6)}-${fileNum}` 
            : null;
          
          // 🔥 HOTFIX: Validar companyId antes de crear el documento
          if (!companyId) {
            console.error('❌ ERROR CRÍTICO: companyId es null o undefined');
            throw new Error('No se pudo determinar la empresa. Por favor, contacta con soporte.');
          }
          
          const documentData = {
            id,
            path,
            downloadURL: downloadURL || null,
            originalName: file.name,
            status: "pending",
            documentType: documentType || "albaran",
            obra: obra || null,
            numeroSerie: generatedNumeroSerie,
            recipients: recipients || [],
            finalEmails: finalEmails || [],
            companyId: companyId,
            uploadedBy: user?.uid || null,
            uploaderEmail: user?.email || userData?.email || null, // 🔥 Guardar email del uploader
            createdAt: serverTimestamp(),
            expiresAt: getExpirationDaysFromNow(30),
            originalRequestId: fromSolicitud && requestId ? requestId : null,
            // Metadata de lote
            batchId: files.length > 1 ? `batch-${Date.now()}` : null,
            batchIndex: fileNum,
            batchTotal: files.length,
          };
          
          await setDoc(doc(db, COLLECTIONS.REQUESTS, id), documentData);

          const url = buildSignatureLink(id);
          
          // Guardar resultado
          uploadedResults.push({
            id,
            fileName: file.name,
            link: url,
            path,
            numeroSerie: generatedNumeroSerie,
            success: true
          });
          
          console.log(`✅ [${fileNum}/${files.length}] Subido: ${file.name}`);
          
        } catch (fileError) {
          console.error(`❌ Error subiendo ${file.name}:`, fileError);
          uploadedResults.push({
            fileName: file.name,
            success: false,
            error: fileError.message
          });
        }
      }

      // 🔄 Actualizar estado de la solicitud original si viene desde ahí
      if (fromSolicitud && requestId && uploadedResults.some(r => r.success)) {
        try {
          const collection_name = requestType === 'albaran' ? 'albaran_requests' : 'work_requests';
          const solicitudRef = doc(db, collection_name, requestId);
          
          // Obtener los datos actuales para preservar información de Kanban
          const solicitudSnap = await getDoc(solicitudRef);
          const solicitudData = solicitudSnap.data();
          
          const updateData = {
            status: solicitudData?.status === 'pendiente' ? 'procesada' : 'procesada',
            updatedAt: serverTimestamp(),
            processedAt: serverTimestamp(),
            documentosIds: uploadedResults.filter(r => r.success).map(r => r.id),
          };
          
          // Si tiene kanban_card_id, preservarlo
          if (solicitudData?.kanban_card_id) {
            updateData.kanban_card_id = solicitudData.kanban_card_id;
          }
          
          await setDoc(solicitudRef, updateData, { merge: true });
          
          console.log(`✅ Estado de solicitud actualizado a 'procesada'${solicitudData?.kanban_card_id ? ` [Kanban: ${solicitudData.kanban_card_id}]` : ''}`);
        } catch (error) {
          console.error("❌ Error actualizando estado de solicitud:", error);
        }
      }

      setUploadedDocs(uploadedResults);
      
      const successCount = uploadedResults.filter(r => r.success).length;
      const failCount = uploadedResults.filter(r => !r.success).length;
      
      if (successCount > 0) {
        showToast({ 
          type: "success", 
          message: `✅ ${successCount} documento(s) subido(s) correctamente${failCount > 0 ? ` (${failCount} fallaron)` : ''}` 
        });
      } else {
        showToast({ 
          type: "error", 
          message: `❌ Error: No se pudo subir ningún documento` 
        });
      }

    } catch (err) {
      console.error(err);
      showToast({ type: "error", message: "Error subiendo archivos" });
    } finally {
      setLoading(false);
      setUploadProgress({});
    }
  };

  // ====== Generar PDF con página QR para un documento específico ======
  async function handleGenerateQrPdf(doc) {
    if (!APPEND_QR_URL) {
      showToast({ type: "error", message: "Falta VITE_APPEND_QR_URL en .env" });
      return;
    }
    if (!doc || !doc.path) {
      showToast({ type: "error", message: "No hay PDF válido" });
      return;
    }
    if (!doc.link) {
      showToast({ type: "error", message: "Falta el enlace de firma" });
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("No hay usuario autenticado");
      const idToken = await user.getIdToken(true);

      showToast({ type: "info", message: `Generando QR para ${doc.fileName}...` });

      const resp = await fetch(APPEND_QR_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          sourcePath: doc.path,
          signUrl: doc.link,
          title: "Escanea para firmar",
          obra: obra || null,
          numeroSerie: doc.numeroSerie || null,
        }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) {
        console.error("appendQrToPdf response:", data);
        const errorMsg = data.error || `Error ${resp.status}: ${resp.statusText}`;
        showToast({ type: "error", message: `Error generando QR: ${errorMsg}` });
        return;
      }

      // Actualizar el documento con la URL del QR
      setUploadedDocs(prev => prev.map(d => 
        d.id === doc.id ? { ...d, qrPdfUrl: data.signedUrl } : d
      ));

      showToast({ type: "success", message: `✅ QR generado para ${doc.fileName}` });
    } catch (e) {
      console.error("appendQrToPdf error:", e);
      showToast({ type: "error", message: `Error generando QR: ${e.message}` });
    }
  }

  // ===== WhatsApp Sharing =====
  function getWhatsAppMessage(doc) {
    if (!doc || !doc.link) return "Sin enlace";
    
    const link = doc.link;
    
    // Build WhatsApp message: use custom message + append link
    let text = message || "";
    // Remove any leftover {link} placeholder and "Enlace:" lines
    text = text
      .replace(/\{link\}/g, "")
      .replace(/^.*[Ee]nlace.*$/gm, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    // Append the actual link at the end
    text = `${text}\n\n${link}`;
    
    return text;
  }

  // Share via WhatsApp (no specific number) - para un documento específico
  function shareWhatsAppNoNumber(doc) {
    if (!doc || !doc.link) {
      showToast({ type: "error", message: "Primero genera el enlace" });
      return;
    }
    
    const text = getWhatsAppMessage(doc);
    const url = buildWhatsAppLink({ phone: null, text });
    window.open(url, "_blank", "noopener,noreferrer");
  }

  // Share via WhatsApp to specific number - para un documento específico
  function shareWhatsAppTo(phone, doc) {
    if (!doc || !doc.link) {
      showToast({ type: "error", message: "Primero genera el enlace" });
      return;
    }
    
    const text = getWhatsAppMessage(doc);
    const url = buildWhatsAppLink({ phone, text });
    window.open(url, "_blank", "noopener,noreferrer");
  }

  // Copiar enlace de un documento específico
  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      showToast({ type: "success", message: "Enlace copiado al portapapeles" });
    } catch {
      showToast({ type: "error", message: "No se pudo copiar el enlace" });
    }
  };

  // 🛡️ Enviar email PRL al responsable
  const sendPRLEmail = async () => {
    if (!solicitudData?.solicitarPRL) {
      showToast({ type: "error", message: "Esta solicitud no tiene PRL" });
      return;
    }

    if (!solicitudData?.responsablePRL?.email) {
      showToast({ type: "error", message: "No hay email del responsable PRL" });
      return;
    }

    if (!SEND_URL) {
      showToast({ type: "error", message: "Falta VITE_SEND_EMAIL_URL en tu .env" });
      return;
    }

    try {
      setSending(true);

      const user = auth.currentUser;
      if (!user) throw new Error("No hay usuario autenticado");
      const idToken = await user.getIdToken(true);

      const baseUrl = import.meta.env.VITE_BASE_URL || 'https://firma-digital-db89b.web.app';
      const solicitudId = solicitudData.id || searchParams.get('requestId');

      if (!solicitudId) {
        throw new Error("No se pudo obtener el ID de la solicitud");
      }

      // Enlace correcto a la ruta React del formulario PRL
      const prlLink = `${baseUrl}/prl/${solicitudId}`;
      const responsable = solicitudData.responsablePRL;

      console.log('📧 Enviando email PRL:');
      console.log('  - Solicitud ID:', solicitudId);
      console.log('  - Enlace PRL:', prlLink);
      console.log('  - Responsable:', responsable.nombre);

      const emailHtml = `
        <div style="font-family:system-ui,Arial,sans-serif;max-width:600px;margin:0 auto;">
          <div style="background:#0b1028;padding:20px;border-radius:12px 12px 0 0;">
            <h1 style="color:#e9eefc;margin:0;font-size:24px;">🛡️ FORMULARIO PRL - ${solicitudData.codigoObra}</h1>
          </div>
          
          <div style="background:#fff;padding:24px;border-radius:0 0 12px 12px;">
            <div style="background:#fef3c7;padding:16px;border-radius:8px;margin-bottom:20px;border-left:4px solid #f59e0b;">
              <p style="margin:0 0 8px 0;color:#92400e;font-weight:bold;">
                ⚠️ ACCIÓN REQUERIDA: Debe completar el formulario PRL
              </p>
              <p style="margin:0;color:#92400e;font-size:14px;">
                Hola <strong>${responsable.nombre}</strong>, debe completar el formulario de Prevención de Riesgos Laborales para la siguiente obra.
              </p>
            </div>

            <h3 style="color:#333;border-bottom:2px solid #eee;padding-bottom:8px;margin-top:0;">DATOS DE LA OBRA</h3>
            <table style="width:100%;margin-bottom:20px;">
              <tr><td style="padding:4px 0;"><strong>Código de Obra:</strong></td><td style="padding:4px 0;">${solicitudData.codigoObra}</td></tr>
              <tr><td style="padding:4px 0;"><strong>Fechas:</strong></td><td style="padding:4px 0;">${solicitudData.fechasActuacion || solicitudData.rangoFechas}</td></tr>
              <tr><td style="padding:4px 0;"><strong>Dirección:</strong></td><td style="padding:4px 0;">${solicitudData.direccionObra}</td></tr>
              <tr><td style="padding:4px 0;"><strong>Responsable PRL:</strong></td><td style="padding:4px 0;">${responsable.nombre}</td></tr>
            </table>

            <div style="background:#dcfce7;padding:20px;border-radius:8px;margin:20px 0;text-align:center;border-left:4px solid #10b981;">
              <p style="margin:0 0 16px 0;color:#047857;font-weight:bold;font-size:16px;">
                👇 Haga clic en el botón para abrir el formulario
              </p>
              <a href="${prlLink}" style="display:inline-block;background:#1a3a6b;color:white;padding:14px 32px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:16px;box-shadow:0 4px 12px rgba(26,58,107,0.3);">
                📋 RELLENAR FORMULARIO PRL
              </a>
            </div>

            <div style="background:#f3f4f6;padding:16px;border-radius:8px;margin-top:20px;">
              <p style="margin:0 0 8px 0;color:#374151;font-weight:bold;font-size:14px;">
                📌 Instrucciones:
              </p>
              <ol style="margin:8px 0;padding-left:20px;color:#6b7280;font-size:13px;">
                <li style="margin-bottom:8px;">Haga clic en el botón "RELLENAR FORMULARIO PRL" arriba</li>
                <li style="margin-bottom:8px;">Complete todos los campos del formulario</li>
                <li style="margin-bottom:8px;">Firme en el recuadro de firma</li>
                <li style="margin-bottom:8px;"><strong>Pulse el botón "Generar PDF"</strong> al final del formulario</li>
                <li style="margin-bottom:0;">El PDF se enviará automáticamente por email a <strong>ptrabajo@cejm.eu</strong> y a usted</li>
              </ol>
            </div>

            <hr style="border:none;border-top:1px solid #eee;margin:24px 0"/>
            <p style="color:#999;font-size:12px;margin-bottom:0;text-align:center;">
              Sistema de Gestión CEJM - Prevención de Riesgos Laborales
            </p>
          </div>
        </div>
      `;

      const res = await fetch(SEND_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          to: [responsable.email],
          subject: `🛡️ PRL - Obra ${solicitudData.codigoObra}`,
          html: emailHtml,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        throw new Error(data.error || `Error ${res.status}`);
      }

      showToast({ 
        type: "success", 
        message: `✅ Email PRL enviado a ${responsable.nombre}` 
      });

    } catch (error) {
      console.error("Error enviando email PRL:", error);
      showToast({ 
        type: "error", 
        message: `Error enviando email PRL: ${error.message}` 
      });
    } finally {
      setSending(false);
    }
  };

  // Envío por Cloud Function (invitación a firmar) - para todos los documentos subidos
  const sendEmailsNow = async () => {
    const emails = recipients.filter(r => r.type === "email").map(r => r.value);
    if (!emails.length) {
      showToast({ type: "error", message: "No hay destinatarios de correo" });
      return;
    }
    if (uploadedDocs.length === 0 || !uploadedDocs.some(d => d.success)) {
      showToast({ type: "error", message: "Primero genera los enlaces" });
      return;
    }
    if (!SEND_URL) {
      showToast({ type: "error", message: "Falta VITE_SEND_EMAIL_URL en tu .env" });
      return;
    }

    setSending(true);
    const results = {};

    try {
      const successDocs = uploadedDocs.filter(d => d.success);
      
      const user = auth.currentUser;
      if (!user) throw new Error("No hay usuario autenticado");
      const idToken = await user.getIdToken(true);

      // Enviar un correo por cada documento (cada uno con su enlace correcto)
      let sentCount = 0;
      let errorCount = 0;

      for (const docItem of successDocs) {
        try {
          // Reemplazar {link} con la URL real del documento
          let emailMessage = message
            .replace(/\{link\}/g, docItem.link)
            .replace(/\n{3,}/g, "\n\n")
            .trim();
          
          const html = buildInvitationEmail({ 
            message: emailMessage, 
            link: docItem.link,
            documentName: docItem.fileName
          });

          const res = await fetch(SEND_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${idToken}`,
            },
            mode: "cors",
            body: JSON.stringify({
              to: emails,
              subject: docItem.fileName.replace('.pdf', ''),
              html,
            }),
          });

          const data = await res.json().catch(() => ({}));

          if (!res.ok || !data.ok) {
            console.error(`sendEmail error for ${docItem.fileName}:`, data);
            errorCount++;
          } else {
            sentCount++;
          }
        } catch (docError) {
          console.error(`Error enviando email para ${docItem.fileName}:`, docError);
          errorCount++;
        }
      }

      if (sentCount > 0) {
        emails.forEach(e => results[e] = "ok");
        showToast({ 
          type: "success", 
          message: `✅ ${sentCount} correo(s) enviado(s) a ${emails.length} destinatario(s)${errorCount > 0 ? ` (${errorCount} fallaron)` : ''}` 
        });

        // 📧 Enviar correo de confirmación resumen a ffernandez@cejm.eu (solo para partes de trabajo)
        if (documentType === "parte_trabajo") {
          try {
            const docsListHtml = successDocs.map((d, i) => `
              <tr>
                <td style="padding:6px 12px;border-bottom:1px solid #e2e8f0;">${i + 1}</td>
                <td style="padding:6px 12px;border-bottom:1px solid #e2e8f0;">${d.fileName}</td>
                <td style="padding:6px 12px;border-bottom:1px solid #e2e8f0;">
                  <a href="${d.link}" style="color:#4f6cf7;">Ver enlace</a>
                </td>
              </tr>`).join('');

            // 🛡️ Sección PRL si existe
            const baseUrl = import.meta.env.VITE_BASE_URL || 'https://firma-digital-db89b.web.app';
            // 🔧 FIX: Usar el ID real de la solicitud
            const solicitudId = solicitudData?.id || searchParams.get('requestId');
            const prlSection = solicitudData?.solicitarPRL ? `
              <div style="background:#fef3c7;padding:16px;border-radius:8px;margin:20px 0;border-left:4px solid #f59e0b;">
                <h3 style="color:#92400e;margin:0 0 12px 0;font-size:16px;">
                  🛡️ Prevención de Riesgos Laborales (PRL)
                </h3>
                <p style="margin:0 0 8px 0;color:#78350f;">
                  <strong>Estado:</strong> ${solicitudData.prlCompletado ? '✅ Completado' : '⏳ Pendiente de completar'}
                </p>
                <p style="margin:0 0 16px 0;color:#78350f;">
                  <strong>Responsable:</strong> ${solicitudData.responsablePRL?.nombre || 'N/A'}
                  ${solicitudData.responsablePRL?.email ? ` (${solicitudData.responsablePRL.email})` : ''}
                </p>
                ${!solicitudData.prlCompletado ? `
                  <div style="text-align:center;">
                    <a href="${baseUrl}/prl/${solicitudId}" 
                       target="_blank"
                       style="display:inline-block;background:#f59e0b;color:#fff;text-decoration:none;
                              padding:10px 20px;border-radius:6px;font-weight:600;font-size:14px;">
                      🛡️ Completar Formulario PRL
                    </a>
                  </div>
                ` : `
                  <div style="background:#fff;padding:12px;border-radius:6px;margin-top:12px;">
                    <p style="margin:0 0 8px 0;color:#065f46;font-weight:bold;">✅ PRL Completado</p>
                    ${solicitudData.prlDatos?.riesgosIdentificados ? `
                      <p style="margin:8px 0 4px 0;color:#374151;font-size:13px;"><strong>Riesgos:</strong></p>
                      <p style="margin:0;color:#6b7280;font-size:12px;">${solicitudData.prlDatos.riesgosIdentificados.substring(0, 150)}${solicitudData.prlDatos.riesgosIdentificados.length > 150 ? '...' : ''}</p>
                    ` : ''}
                  </div>
                `}
              </div>
            ` : '';

            const confirmHtml = `
              <div style="font-family:system-ui,Arial,sans-serif;max-width:600px;margin:0 auto;">
                <div style="background:#0b1028;padding:20px;border-radius:12px 12px 0 0;">
                  <h1 style="color:#e9eefc;margin:0;font-size:22px;">🔧 Partes de Trabajo Enviados</h1>
                </div>
                <div style="background:#fff;padding:24px;border-radius:0 0 12px 12px;">
                  <p style="color:#374151;">Se han enviado <strong>${sentCount} parte(s) de trabajo</strong> para firmar.</p>
                  ${obra ? `<p style="color:#374151;"><strong>Obra:</strong> ${obra}</p>` : ''}
                  <p style="color:#374151;"><strong>Enviados a:</strong> ${emails.join(', ')}</p>
                  <table style="width:100%;border-collapse:collapse;margin-top:16px;">
                    <thead>
                      <tr style="background:#f8fafc;">
                        <th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e2e8f0;">#</th>
                        <th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e2e8f0;">Documento</th>
                        <th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e2e8f0;">Enlace</th>
                      </tr>
                    </thead>
                    <tbody>${docsListHtml}</tbody>
                  </table>
                  ${prlSection}
                  <hr style="border:none;border-top:1px solid #eee;margin:24px 0"/>
                  <p style="color:#9ca3af;font-size:12px;">Sistema de Gestión CEJM</p>
                </div>
              </div>`;

            await fetch(SEND_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
              body: JSON.stringify({
                to: ["ptrabajo@cejm.eu"],
                subject: `✅ ${sentCount} parte(s) enviado(s) para firmar${obra ? ` - ${obra}` : ''}`,
                html: confirmHtml,
              }),
            });
            console.log('✅ Correo de confirmación enviado a ptrabajo@cejm.eu');
          } catch(e) {
            console.warn('⚠️ No se pudo enviar correo de confirmación partes:', e.message);
          }
        }

        // 📧 Correo confirmación albaranes → kevin15sandoval@gmail.com
        if (documentType === "albaran") {
          try {
            const docsListHtml = successDocs.map((d, i) => `
              <tr>
                <td style="padding:6px 12px;border-bottom:1px solid #e2e8f0;">${i + 1}</td>
                <td style="padding:6px 12px;border-bottom:1px solid #e2e8f0;">${d.fileName}</td>
                <td style="padding:6px 12px;border-bottom:1px solid #e2e8f0;">
                  <a href="${d.link}" style="color:#4f6cf7;">Ver enlace</a>
                </td>
              </tr>`).join('');

            const confirmHtml = `
              <div style="font-family:system-ui,Arial,sans-serif;max-width:600px;margin:0 auto;">
                <div style="background:#0b1028;padding:20px;border-radius:12px 12px 0 0;">
                  <h1 style="color:#e9eefc;margin:0;font-size:22px;">📦 Albaranes Enviados para Firmar</h1>
                </div>
                <div style="background:#fff;padding:24px;border-radius:0 0 12px 12px;">
                  <p style="color:#374151;">Se han enviado <strong>${sentCount} albarán(es)</strong> para firmar.</p>
                  ${obra ? `<p style="color:#374151;"><strong>Obra:</strong> ${obra}</p>` : ''}
                  <p style="color:#374151;"><strong>Enviados a:</strong> ${emails.join(', ')}</p>
                  <table style="width:100%;border-collapse:collapse;margin-top:16px;">
                    <thead>
                      <tr style="background:#f8fafc;">
                        <th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e2e8f0;">#</th>
                        <th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e2e8f0;">Documento</th>
                        <th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e2e8f0;">Enlace</th>
                      </tr>
                    </thead>
                    <tbody>${docsListHtml}</tbody>
                  </table>
                  <hr style="border:none;border-top:1px solid #eee;margin:24px 0"/>
                  <p style="color:#9ca3af;font-size:12px;margin:0;">Sistema de Gestión CEJM</p>
                </div>
              </div>`;

            await fetch(SEND_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
              body: JSON.stringify({
                to: ["kevin15sandoval@gmail.com"],
                subject: `✅ ${sentCount} albarán(es) enviado(s) para firmar${obra ? ` - ${obra}` : ''}`,
                html: confirmHtml,
              }),
            });
            console.log('✅ Correo de confirmación albaranes enviado a kevin15sandoval@gmail.com');
          } catch(e) {
            console.warn('⚠️ No se pudo enviar correo de confirmación albaranes:', e.message);
          }
        }
      } else {
        emails.forEach(e => results[e] = "error");
        showToast({ type: "error", message: "Error al enviar los correos" });
      }
    } catch (e) {
      console.error("sendEmail fetch error:", e);
      emails.forEach(addr => results[addr] = "error");
      showToast({ type: "error", message: `Error de conexión: ${e.message}` });
    } finally {
      setSendResult(results);
      setSending(false);
    }
  };

  return (
    <div className="container">
      <div className="header">
        <span className="badge">FIRMAS CEJM</span>
      </div>

      <h1 className="title">
        {fromSolicitud ? 
          (requestType === 'albaran' ? "📦 Crear Albarán (desde solicitud)" : "📄 Crear Parte de Trabajo (desde solicitud)") 
          : "Subir documento para firmar"}
      </h1>

      {fromSolicitud && (
        <div style={{ 
          background: '#f0fdf4', 
          padding: '16px', 
          borderRadius: '10px', 
          border: '1px solid #bbf7d0',
          marginBottom: '16px'
        }}>
          <p style={{ margin: '0 0 8px 0', color: '#166534', fontWeight: 600, fontSize: 14 }}>
            ✅ Creando parte de trabajo desde solicitud enviada
          </p>
          <p style={{ margin: '0 0 8px 0', color: '#475569', fontSize: 13 }}>
            Datos pre-rellenados automáticamente:
          </p>
          <ul style={{ margin: '0 0 0 20px', padding: 0, color: '#475569', fontSize: 13 }}>
            <li>🏗️ Obra y tipo de documento</li>
            <li>👥 Contactos de la obra</li>
            <li>📝 Mensaje personalizado con detalles</li>
          </ul>
          <p style={{ margin: '8px 0 0 0', fontStyle: 'italic', color: '#64748b', fontSize: 12 }}>
            Solo sube el PDF y ajusta si es necesario
          </p>
        </div>
      )}

      <div className="card grid">
        {/* 🔧 Selector de tipo de documento */}
        <div className="grid">
          <p className="kicker">Tipo de documento</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", background: "#e9eef5", borderRadius: 10, padding: 4, width: "fit-content" }}>
            <DocumentTypeGuard documentType="albaran" action="create">
              <button
                type="button"
                onClick={() => { if (!fromSolicitud) setDocumentType("albaran"); }}
                disabled={fromSolicitud}
                style={{
                  padding: "8px 20px",
                  borderRadius: 7,
                  border: "none",
                  background: documentType === "albaran" ? "#ffffff" : "transparent",
                  color: documentType === "albaran" ? "#0f172a" : "#64748b",
                  fontWeight: documentType === "albaran" ? 600 : 400,
                  fontSize: 13,
                  cursor: fromSolicitud ? "not-allowed" : "pointer",
                  boxShadow: documentType === "albaran" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  transition: "all 0.15s",
                  display: "flex", alignItems: "center", gap: 6,
                  opacity: fromSolicitud && documentType !== "albaran" ? 0.4 : 1,
                }}
              >
                📄 Albarán
              </button>
            </DocumentTypeGuard>

            <DocumentTypeGuard documentType="parte_trabajo" action="create">
              <button
                type="button"
                onClick={() => { if (!fromSolicitud) setDocumentType("parte_trabajo"); }}
                disabled={fromSolicitud}
                style={{
                  padding: "8px 20px",
                  borderRadius: 7,
                  border: "none",
                  background: documentType === "parte_trabajo" ? "#ffffff" : "transparent",
                  color: documentType === "parte_trabajo" ? "#0f172a" : "#64748b",
                  fontWeight: documentType === "parte_trabajo" ? 600 : 400,
                  fontSize: 13,
                  cursor: fromSolicitud ? "not-allowed" : "pointer",
                  boxShadow: documentType === "parte_trabajo" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  transition: "all 0.15s",
                  display: "flex", alignItems: "center", gap: 6,
                  opacity: fromSolicitud && documentType !== "parte_trabajo" ? 0.4 : 1,
                }}
              >
                🔧 Parte de Trabajo {fromSolicitud && documentType === "parte_trabajo" && "✅"}
              </button>
            </DocumentTypeGuard>
          </div>
          
          {/* 🛡️ Botón de Enviar PRL - Visible siempre si la solicitud tiene PRL */}
          {documentType === "parte_trabajo" && 
           fromSolicitud && 
           solicitudData?.solicitarPRL && (
            <div style={{ marginTop: '16px' }}>
              <button 
                className="btn" 
                type="button"
                disabled={sending} 
                onClick={sendPRLEmail}
                style={{ 
                  background: sending ? '#94a3b8' : '#f59e0b',
                  padding: '14px 24px',
                  fontSize: 15,
                  fontWeight: 600,
                  borderRadius: 8,
                  width: '100%',
                  justifyContent: 'center',
                  cursor: sending ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: sending ? 'none' : '0 2px 4px rgba(245,158,11,0.3)'
                }}
              >
                {sending ? "Enviando…" : (
                  <>
                    🛡️ Enviar PRL a {solicitudData.responsablePRL?.nombre || 'responsable'}
                  </>
                )}
              </button>
              <p className="meta" style={{ marginTop: 8, textAlign: 'center' }}>
                {solicitudData.prlCompletado ? 
                  "✅ PRL ya completado" : 
                  "⏳ Envía el formulario PRL al responsable antes de iniciar los trabajos"
                }
              </p>
            </div>
          )}
          
          {/* Show permission warning if user has no access to any document type */}
          {allowedTypes.length === 0 && (
            <div style={{ 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              borderRadius: '8px', 
              padding: '12px', 
              marginTop: '12px' 
            }}>
              <p style={{ margin: 0, color: '#dc2626', fontSize: '14px' }}>
                ⚠️ No tienes permisos para crear documentos. Contacta con tu administrador.
              </p>
            </div>
          )}
          
          {/* Show info about current permissions */}
          {allowedTypes.length > 0 && (
            <div style={{ 
              background: '#f0f9ff', 
              border: '1px solid #bae6fd', 
              borderRadius: '8px', 
              padding: '12px', 
              marginTop: '12px' 
            }}>
              <p style={{ margin: 0, color: '#0369a1', fontSize: '14px' }}>
                ℹ️ Puedes crear: {allowedTypes.map(type => 
                  type === 'albaran' ? 'Albaranes' : 'Partes de Trabajo'
                ).join(', ')}
              </p>
            </div>
          )}
          
          {/* 🏗️ Campo Obra - Para todos los documentos */}
          <div style={{ marginTop: 12 }}>
            <input
              placeholder={documentType === "albaran" ? "Nombre de la obra (para agrupar albaranes)" : "Nombre de la obra (para agrupar partes de trabajo)"}
              value={obra}
              onChange={(e) => setObra(e.target.value)}
              style={{ 
                fontWeight: 600, 
                borderColor: fromSolicitud ? '#10b981' : undefined,
                color: fromSolicitud ? '#166534' : undefined,
                background: fromSolicitud ? '#f0fdf4' : undefined
              }}
              readOnly={fromSolicitud}
            />
            <p className="meta" style={{ marginTop: 4 }}>
              {fromSolicitud ? 
                "✅ Obra cargada desde la solicitud enviada" :
                "💡 Los documentos se agruparán por obra para facilitar la gestión"
              }
            </p>
          </div>
        </div>

        {/* Dropzone - Ahora acepta múltiples archivos */}
        <div
          className={`drop ${drag ? "drag" : ""}`}
          onDragEnter={() => setDrag(true)}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault(); setDrag(false);
            const fileList = e.dataTransfer.files;
            console.log('🎯 Drop event - archivos:', fileList.length);
            // Si ya hay archivos, acumular; si no, reemplazar
            onFiles(fileList, files.length > 0);
          }}
          onClick={(e) => {
            // Solo abrir el selector si no se hizo clic en el input directamente
            if (e.target !== inputRef.current) {
              inputRef.current?.click();
            }
          }}
        >
          <p className="kicker">Arrastra tus PDFs o haz clic</p>
          <p className="meta">
            {files.length > 0 
              ? `${files.length} archivo(s) seleccionado(s)` 
              : "Puedes seleccionar uno o varios archivos (máx. 10 MB cada uno)"}
          </p>
          <br />
          <label 
            className="label-file" 
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            📄 Seleccionar archivo(s)
          </label>
          <p className="meta" style={{ marginTop: 8, fontSize: 11 }}>
            💡 Tip: Mantén presionada la tecla Ctrl (o Cmd en Mac) para seleccionar múltiples archivos a la vez
          </p>
        </div>

        {/* Lista de archivos seleccionados */}
        {files.length > 0 && (
          <div style={{ 
            background: '#f8fafc', 
            padding: '14px', 
            borderRadius: '10px', 
            border: '1px solid #e2e8f0' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <p className="kicker" style={{ margin: 0, color: '#0f172a', fontWeight: 600 }}>
                📋 Archivos seleccionados ({files.length})
              </p>
              <button
                type="button"
                className="copy"
                onClick={() => { inputRef.current?.click(); }}
                style={{ padding: '5px 14px', fontSize: 12 }}
              >
                ➕ Agregar más
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {files.map((f, idx) => (
                <div key={idx} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: '#ffffff',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0'
                }}>
                  <span className="meta" style={{ flex: 1 }}>
                    {idx + 1}. {f.name} ({(f.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {uploadProgress[f.name] !== undefined && (
                      <span className="meta" style={{ color: '#10b981' }}>
                        {uploadProgress[f.name]}%
                      </span>
                    )}
                    <button
                      type="button"
                      className="copy"
                      onClick={() => removeFile(f.name)}
                      style={{ padding: '2px 8px', fontSize: 11 }}
                      disabled={loading}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input oculto para agregar más archivos */}
        <input
          ref={inputRef}
          className="input-file"
          type="file"
          accept="application/pdf"
          multiple
          onChange={(e) => {
            console.log('📂 Input onChange - archivos:', e.target.files?.length);
            // Si ya hay archivos, acumular; si no, reemplazar
            onFiles(e.target.files, files.length > 0);
          }}
          onClick={(e) => {
            // Resetear el valor para permitir seleccionar los mismos archivos de nuevo
            e.target.value = null;
          }}
          style={{ display: 'none' }}
        />

        {/* Destinatarios (invitación / whatsapp) */}
        <div className="grid">
          <p className="kicker">
            Destinatarios {fromSolicitud && "✅ (pre-rellenados desde solicitud)"}
          </p>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              onChange={(e) => {
                const id = e.target.value;
                const c = contacts.find(x => x.id === id);
                pickContact(c);
                e.target.value = "";
              }}
              defaultValue=""
              disabled={contactsLoading}
            >
              <option value="" disabled>
                {contactsLoading ? "Cargando contactos…" : "Elegir de contactos guardados…"}
              </option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name ? `${c.name} — ${c.value}` : c.value} ({c.type})
                </option>
              ))}
            </select>
            <span className="meta">o añadir uno nuevo:</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr auto", gap: 8 }}>
            <input
              placeholder="Correo o teléfono"
              value={destValue}
              onChange={(e) => setDestValue(e.target.value)}
            />
            <input
              placeholder="Nombre (opcional)"
              value={destName}
              onChange={(e) => setDestName(e.target.value)}
            />
            <button className="btn" type="button" onClick={addRecipient}>Añadir</button>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={saveContact}
              onChange={(e) => setSaveContact(e.target.checked)}
            />
            <span className="meta">Guardar este contacto en la agenda</span>
          </label>

          {!!recipients.length && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {recipients.map((r) => (
                <span key={r.value} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 999, padding: '5px 10px', fontSize: 12, color: '#334155' }}>
                  {r.name ? `${r.name} — ` : ""}{r.value} ({r.type})
                  {r.type === "email" && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#64748b' }}>
                      {sendResult[r.value] === "ok" ? "✅ enviado" : sendResult[r.value] === "error" ? "❌ error" : ""}
                    </span>
                  )}
                  <button className="copy" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => removeRecipient(r.value)}>Quitar</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ✅ Cliente: SOLO recibirá el PDF firmado */}
        {(documentType === "albaran" || documentType === "parte_trabajo") && (
          <div className="grid">
            <p className="kicker">Cliente (solo recibirá el PDF firmado)</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
              <input
                placeholder="Correo del cliente"
                value={finalEmailValue}
                onChange={(e) => setFinalEmailValue(e.target.value)}
              />
              <button className="btn" type="button" onClick={addFinalEmail}>Añadir</button>
            </div>

            {!!finalEmails.length && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {finalEmails.map((em) => (
                  <span key={em} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 999, padding: '5px 10px', fontSize: 12, color: '#334155' }}>
                    📧 {em}
                    <button className="copy" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => removeFinalEmail(em)}>Quitar</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Acciones */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <button 
            className="btn" 
            disabled={
              loading || 
              files.length === 0 || 
              recipients.length === 0 || 
              !canCreateDocumentType(documentType)
            } 
            onClick={handleUpload}
          >
            {loading ? "Subiendo archivos..." : `Subir ${files.length > 0 ? files.length : ''} archivo(s)`}
          </button>
          
          {files.length === 0 && <span className="meta" style={{ color: '#ef4444' }}>❌ Selecciona al menos un PDF</span>}
          {files.length > 0 && recipients.length === 0 && <span className="meta" style={{ color: '#ef4444' }}>❌ Añade al menos un destinatario</span>}
          {files.length > 0 && recipients.length > 0 && !canCreateDocumentType(documentType) && (
            <span className="meta" style={{ color: '#ef4444' }}>
              ❌ No tienes permisos para crear {documentType === 'albaran' ? 'albaranes' : 'partes de trabajo'}
            </span>
          )}
          {files.length > 0 && recipients.length > 0 && canCreateDocumentType(documentType) && (
            <span className="meta" style={{ color: '#10b981' }}>✅ Listo para subir</span>
          )}
        </div>

        {/* Mostrar progreso de subida */}
        {loading && Object.keys(uploadProgress).length > 0 && (
          <div style={{ 
            background: '#f8fafc', 
            padding: '14px', 
            borderRadius: '10px', 
            border: '1px solid #e2e8f0' 
          }}>
            <p className="kicker" style={{ margin: '0 0 8px 0', color: '#0f172a', fontWeight: 600 }}>
              ⏳ Subiendo archivos...
            </p>
            {Object.entries(uploadProgress).map(([fileName, progress]) => (
              <div key={fileName} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span className="meta">{fileName}</span>
                  <span style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>{progress}%</span>
                </div>
                <ProgressBar progress={progress} />
              </div>
            ))}
          </div>
        )}

        {/* Documentos subidos exitosamente */}
        {uploadedDocs.length > 0 && (
          <div className="grid">
            <p className="kicker">
              📋 Documentos subidos ({uploadedDocs.filter(d => d.success).length}/{uploadedDocs.length})
            </p>
            
            {uploadedDocs.map((doc, idx) => (
              <div 
                key={doc.id || idx} 
                style={{ 
                  background: doc.success ? '#f0fdf4' : '#fef2f2', 
                  padding: '16px', 
                  borderRadius: '10px', 
                  border: `1px solid ${doc.success ? '#bbf7d0' : '#fecaca'}`,
                  marginBottom: 12
                }}
              >
                {doc.success ? (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <p style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#166534', fontSize: 14 }}>
                        ✅ {doc.fileName}
                      </p>
                      {doc.numeroSerie && (
                        <p className="meta" style={{ margin: '0 0 4px 0' }}>
                          🔢 Número de serie: {doc.numeroSerie}
                        </p>
                      )}
                    </div>

                    {/* Enlace de firma */}
                    <div style={{ marginBottom: 12 }}>
                      <p className="kicker" style={{ margin: '0 0 4px 0', fontSize: 12 }}>
                        Enlace para firmar:
                      </p>
                      <div className="linkbox">
                        <input 
                          value={doc.link} 
                          readOnly 
                          onFocus={(e) => e.target.select()} 
                          style={{ fontSize: 12 }}
                        />
                        <button className="copy" onClick={() => copyLink(doc.link)}>
                          Copiar
                        </button>
                      </div>
                    </div>

                    {/* Botones de acción */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button 
                        className="copy" 
                        onClick={() => shareWhatsAppNoNumber(doc)}
                        style={{ padding: '6px 12px', fontSize: 12 }}
                      >
                        📱 WhatsApp
                      </button>

                      {documentType === "albaran" && (
                        <>
                          {!doc.qrPdfUrl ? (
                            <button 
                              className="copy" 
                              onClick={() => handleGenerateQrPdf(doc)}
                              style={{ padding: '6px 12px', fontSize: 12 }}
                            >
                              🔲 Generar QR
                            </button>
                          ) : (
                            <a
                              className="copy"
                              href={doc.qrPdfUrl}
                              target="_blank"
                              rel="noreferrer"
                              download={`${doc.fileName.replace('.pdf', '')}_QR.pdf`}
                              style={{ 
                                padding: '6px 12px', 
                                fontSize: 12,
                                textDecoration: 'none',
                                display: 'inline-block'
                              }}
                            >
                              ✅ Descargar con QR
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <div>
                    <p style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#991b1b', fontSize: 14 }}>
                      ❌ {doc.fileName}
                    </p>
                    <p className="meta" style={{ margin: 0, color: '#991b1b' }}>
                      Error: {doc.error || 'Error desconocido'}
                    </p>
                  </div>
                )}
              </div>
            ))}

            {/* Botón para enviar emails de todos los documentos */}
            {uploadedDocs.some(d => d.success) && recipients.some(r => r.type === "email") && (
              <button 
                className="btn" 
                disabled={sending} 
                onClick={sendEmailsNow}
                style={{ 
                  marginTop: 8,
                  background: sending ? '#94a3b8' : '#1e293b',
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  borderRadius: 8,
                  width: '100%',
                  justifyContent: 'center',
                  cursor: sending ? 'not-allowed' : 'pointer'
                }}
              >
                {sending ? "Enviando correos…" : `📧 Enviar ${uploadedDocs.filter(d => d.success).length} documento(s) por correo`}
              </button>
            )}

            {/* 🛡️ Botón para enviar PRL (solo si la solicitud tiene PRL) */}
            {uploadedDocs.some(d => d.success) && 
             documentType === "parte_trabajo" && 
             solicitudData?.solicitarPRL && (
              <button 
                className="btn" 
                disabled={sending} 
                onClick={sendPRLEmail}
                style={{ 
                  marginTop: 8,
                  background: sending ? '#94a3b8' : '#f59e0b',
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  borderRadius: 8,
                  width: '100%',
                  justifyContent: 'center',
                  cursor: sending ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {sending ? "Enviando…" : (
                  <>
                    🛡️ Enviar PRL a {solicitudData.responsablePRL?.nombre || 'responsable'}
                  </>
                )}
              </button>
            )}

            {/* Mensaje personalizado */}
            <div className="grid" style={{ marginTop: 16 }}>
              <p className="kicker">
                Mensaje para el destinatario {fromSolicitud && "✅ (personalizado desde solicitud)"}
              </p>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={fromSolicitud ? 8 : 4}
                style={{
                  width: "100%", resize: "vertical",
                  borderColor: fromSolicitud ? '#10b981' : undefined
                }}
              />
              <p className="meta">
                {fromSolicitud ? 
                  "✅ Mensaje personalizado con detalles de la solicitud. Puedes editarlo si es necesario." :
                  "💡 El mensaje se incluirá en el correo junto con el botón para firmar."
                }
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
